import React, { useEffect, useState } from 'react';
import { FormControl } from '@material-ui/core';
import { InputAdornment, IconButton, Box } from '@mui/material';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import moment from 'moment-timezone';
import MomentUtils from '@date-io/moment';
import { Controller, useFormContext } from 'react-hook-form';
import { generateTestId } from '../../../../../utils';
import { Event, NavigateBeforeOutlined, NavigateNextOutlined } from '@mui/icons-material';

export default function DaySelector({ name, props: { defaultValue, selectedDateFormat, label, interval, valueFormat, variant, disabled, range, displayDefaultAdornment, displayNavIcons, ...rest }, isEdit, referenceFieldMin, referenceFieldMax }) {
    const [error, setError] = useState(null);

    const {
        control,
        formState: { errors, touchedFields },
        getValues,
        selectedDate,
        setValue,
        updateSelectedValues,
        watch,
    } = useFormContext();

    const useSelectedDate = defaultValue === 'selectedDate';
    let minDate = referenceFieldMin && getValues(referenceFieldMin) ? moment(getValues(referenceFieldMin)) : undefined;
    let maxDate = referenceFieldMax && getValues(referenceFieldMax) ? moment(getValues(referenceFieldMax)) : undefined;
    if (range) {
        minDate = moment(useSelectedDate ? selectedDate : undefined).startOf(range.startOf);
        maxDate = moment(useSelectedDate ? selectedDate : undefined).endOf(range.endOf);

        if (range.subtract) {
            minDate = moment().subtract(range.subtract[0], range.subtract[1]);
        }
        if (range.add) {
            maxDate = moment().add(range.add[0], range.add[1]);
        }
    }

    const formatValue = (val) => {
        let formatted = val;
        if (formatted == null) return formatted;
        if (!interval && !valueFormat) return val;
        if (interval) formatted = formatted.startOf(interval);
        if (valueFormat) formatted = formatted.format(valueFormat);
        return formatted;
    };

    useEffect(() => {
        const touched = touchedFields[name];
        const value = getValues(name);
        // If no default values from edit and field is not touched, set the default value
        if (selectedDate && !isEdit && !touched) {
            if (updateSelectedValues) {
                updateSelectedValues(name, moment(selectedDate));
            }
            setValue(name, moment(selectedDate));
        }
        if (useSelectedDate && !value) {
            const formattedSelectedDate = moment(selectedDate, selectedDateFormat);
            if (updateSelectedValues) {
                updateSelectedValues(name, formatValue(formattedSelectedDate));
            }
            setValue(name, formatValue(formattedSelectedDate));
        }
    }, [selectedDate]);

    useEffect(() => {
        setError(errors[name]);
    }, [errors]);

    useEffect(() => {
        const existingValue = getValues(name);
        if (updateSelectedValues) {
            updateSelectedValues(name, existingValue);
        }
    }, [watch(name)]);

    const cancelAdornment = (onChange) => (
        <InputAdornment position="end">
            <IconButton sx={{ m: 0.55 }} onClick={() => onChange(formatValue(null))} edge="end" data-testid={generateTestId(name, 'cancel-btn')}>
                <i className="text-20 material-icons" color="action">
                    cancel
                </i>
            </IconButton>
        </InputAdornment>
    );

    return (
        <FormControl fullWidth style={{ margin: '8px 0' }}>
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue === undefined ? formatValue(moment()) : defaultValue}
                render={({ field: { onChange, value } }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
                        {displayNavIcons && (
                            <IconButton onClick={() => onChange(moment(value).subtract(1, interval))}>
                                <NavigateBeforeOutlined />
                            </IconButton>
                        )}
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                            <DatePicker
                                fullWidth
                                onChange={(val) => onChange(formatValue(val))}
                                label={label}
                                inputVariant={variant || 'standard'}
                                value={value}
                                disabled={disabled}
                                error={!!error}
                                helperText={error?.message}
                                minDate={minDate}
                                maxDate={maxDate}
                                InputProps={{
                                    endAdornment: disabled ? undefined : displayDefaultAdornment ? <Event /> : cancelAdornment(onChange),
                                }}
                                data-testid={generateTestId(name, 'date-picker')}
                                {...rest}
                            />
                        </MuiPickersUtilsProvider>
                        {displayNavIcons && (
                            <IconButton onClick={() => onChange(moment(value).add(1, interval))}>
                                <NavigateNextOutlined />
                            </IconButton>
                        )}
                    </Box>
                )}
            />
        </FormControl>
    );
}
